import {
  Avatar,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  ChipProps,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useMemo } from 'react'
import { Icons, ScrollTag, SkeletonText } from 'utoniq-ui'

import {
  Event,
  Maybe,
  PageInfo,
  PickupUserListItemFragment,
  UserPickupTicketStatus,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'

export * from './fragments'

export type State = {
  loading: boolean
  refetching: boolean
  hasNextPage: boolean
  event: Maybe<Pick<Event, 'id' | 'title'>>
  isHistory: boolean
  type: UserPickupTicketStatus
  pickupUsers:
    | {
        pageInfo: Pick<PageInfo, 'hasNextPage' | 'endCursor'>
        edges: {
          node: PickupUserListItemFragment
        }[]
      }
    | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onFetchMore: () => Promise<void>
  onFilter: (type: UserPickupTicketStatus) => Promise<void>
}

export const MyOwnedTicketsTemplate: Template<State, Action> = ({
  state,
  onFetchMore,
  onFilter,
}) => {
  const { t } = useTranslation('myOwnedTickets')
  const { routerPages, generatePath, router } = useRouter<{ eventId: string }>()
  return (
    <BaseLayout
      margin
      header={{
        back: true,
        title: t(state.isHistory ? 'titleHistory' : 'title'),
      }}
    >
      {!state.isHistory && (
        <Stack alignItems="flex-end" flex={1} sx={{ py: 1 }}>
          <Link
            variant="subtitle1"
            href={generatePath(routerPages.MyOwnedTicketsHistory, {
              eventId: router.query.eventId,
            })}
          >
            {t('titleHistory', '過去の受け取り商品')}
          </Link>
        </Stack>
      )}
      <FilterMenu
        type={state.type}
        onFilter={onFilter}
        isHistory={state.isHistory}
      />
      <MyOwnedTicketCell
        type={state.type}
        pickupUsers={state.pickupUsers}
        loading={state.loading || state.refetching}
        event={state.event}
        hasNextPage={state.hasNextPage}
        onFetchMore={onFetchMore}
      />
    </BaseLayout>
  )
}

type MyOwnedTicketCellProps = {
  pickupUsers: State['pickupUsers']
  event: State['event']
  loading: State['loading']
  hasNextPage: State['hasNextPage']
  onFetchMore: Action['onFetchMore']
  type: State['type']
}
export const MyOwnedTicketCell: Cell<MyOwnedTicketCellProps> = ({
  pickupUsers,
  event,
  loading,
  hasNextPage,
  onFetchMore,
  type,
}) => {
  const { t } = useTranslation('myOwnedTickets')

  return (
    <CellFrame>
      <Typography variant="h6" sx={{ py: 2 }}>
        {event?.title}
      </Typography>

      <ScrollFetcher
        nodes={pickupUsers?.edges.map(({ node }, index) => (
          <OwnedTicket pickupUser={node} key={index} index={index} />
        ))}
        emptyMessage={t(`myOwnedTicketEmptyMessage.${type}`)}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        loadingComponent={
          <Stack spacing={2}>
            <OwnedTicketSkeleton />
            <OwnedTicketSkeleton />
            <OwnedTicketSkeleton />
          </Stack>
        }
      />
    </CellFrame>
  )
}

type OwnedTicketProps = {
  pickupUser: PickupUserListItemFragment
  index: number
}
const OwnedTicket: Cell<OwnedTicketProps> = ({ pickupUser, index }) => {
  const { t } = useTranslation('myOwnedTickets')
  const { generatePath, routerPages } = useRouter()
  const { analytics } = useAnalytics()

  const remainingTime = useMemo(() => {
    if (!pickupUser.pickupRemainingTime) return null

    const { hours, minutes, expiring } = pickupUser.pickupRemainingTime
    const remainingTime =
      hours || minutes
        ? [
            hours && hours > 0 ? `${hours}${t('time')}` : '',
            minutes > 0 ? `${minutes}${t('minute')}` : '',
          ].join('')
        : 1

    return expiring
      ? t('countDownTime', { remainingTime })
      : t('countDownTimeUntilPickupStart', { remainingTime })
  }, [pickupUser.pickupRemainingTime, t])

  if (!pickupUser.eventCollection?.collection?.id) return null

  return (
    <CellFrame>
      <Card elevation={0}>
        <CardActionArea
          href={generatePath(routerPages.MyOwnedTicketDetail, {
            eventId: pickupUser.event.id,
            collectionId: pickupUser.eventCollection.collection.id,
            pickupUserId: pickupUser.id,
          })}
          onClick={() => {
            analytics?.track('click_select', {
              artistId: pickupUser.eventCollection.collection.artistId,
              eventId: pickupUser.event.id,
              collectionId: pickupUser.eventCollection.collection.id,
              name: 'eventPickup',
            })
          }}
        >
          <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Avatar
                variant="square"
                src={
                  pickupUser.eventCollection.collection?.thumbnail?.uri ?? ''
                }
                sx={{ width: 72, height: 72 }}
              />
              <Stack flex={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ pb: 2 }}
                >
                  <ReceiptTag
                    receipted={!!pickupUser.isCompleted}
                    expired={!!pickupUser.isExpired}
                  />
                  {remainingTime && (
                    <Stack direction="row" alignItems="center">
                      <Icons.Notice
                        fontSize="xSmall"
                        sx={{
                          color: pickupUser.pickupRemainingTime?.expiring
                            ? 'like.tap'
                            : 'primary.main',
                        }}
                      />
                      <Typography
                        variant="overline"
                        sx={{
                          color: pickupUser.pickupRemainingTime?.expiring
                            ? 'like.tap'
                            : 'primary.main',
                        }}
                      >
                        {remainingTime}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
                <Typography variant="subtitle1">
                  {pickupUser.eventCollection.collection?.title}
                </Typography>
                <Stack sx={{ pt: 1 }}>
                  <Stack direction="row" spacing={1}>
                    <Typography variant="overline" color="text.secondary">
                      {t('pickupTime', '受け取り時間：')}
                    </Typography>
                    <Typography variant="overline" color="text.secondary">
                      {pickupUser.formattedPickupStartEndTime}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Typography variant="overline" color="text.secondary">
                      {t('pickupLocation', '受け取り場所：')}
                    </Typography>

                    <Typography variant="overline" color="text.secondary">
                      {pickupUser.eventCollection.pickupLocation}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Stack
              flex={1}
              spacing={1}
              sx={{ mt: 4, bgcolor: 'grey.100', borderRadius: 1, px: 4, py: 2 }}
            >
              <Typography variant="overline">
                {t('items', '商品内容')}
              </Typography>

              <Typography variant="overline" color="text.secondary">
                {pickupUser.itemsString}
              </Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </CellFrame>
  )
}

const OwnedTicketSkeleton = () => (
  <CellFrame>
    <Card elevation={0}>
      <CardActionArea>
        <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Skeleton variant="rectangular" width={90} height={64} />
            <Stack flex={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
                sx={{ pb: 2 }}
              >
                <Stack flex={1}>
                  <SkeletonText variant="medium" />
                </Stack>
                <Stack flex={3}>
                  <SkeletonText variant="small" width="80%" />
                </Stack>
              </Stack>
              <SkeletonText variant="subtitle1" />
              <Stack sx={{ pt: 1 }}>
                <SkeletonText variant="caption" width="60%" />
              </Stack>
            </Stack>
          </Stack>
          <Stack
            flex={1}
            spacing={1}
            sx={{ mt: 4, bgcolor: 'grey.100', borderRadius: 1, px: 4, py: 2 }}
          >
            <SkeletonText
              variant="caption"
              width="20%"
              sx={{ bgcolor: 'grey.200' }}
            />
            <SkeletonText
              variant="caption"
              width="75%"
              sx={{ bgcolor: 'grey.200' }}
            />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  </CellFrame>
)

type ReceiptProps = {
  receipted?: boolean
  expired?: boolean
}
export const ReceiptTag: Cell<ReceiptProps> = ({ receipted, expired }) => {
  const { t } = useTranslation('myOwnedTickets')

  const { label, color, bgcolor, variant } = useMemo<{
    label: string
    color: string
    bgcolor?: string
    variant: ChipProps['variant']
  }>(() => {
    if (expired) {
      return {
        label: t('expired'),
        color: 'text.secondary',
        bgcolor: 'grey.100',
        variant: 'filled',
        disabled: false,
      }
    }
    if (receipted) {
      return {
        label: t('isCompleted'),
        color: 'text.disabled',
        variant: 'outlined',
      }
    }
    return {
      label: t('isNotCompleted'),
      color: 'white',
      bgcolor: 'primary.main',
      variant: 'filled',
    }
  }, [expired, receipted, t])

  return (
    <Chip
      size="small"
      variant={variant}
      sx={{
        height: 20,
        borderRadius: '2px',
        bgcolor,
        py: 1,
        '.MuiChip-label': {
          px: 1,
        },
      }}
      label={
        <Typography
          color={color}
          variant="subtitle2"
          sx={{ whiteSpace: 'nowrap' }}
        >
          {label}
        </Typography>
      }
    />
  )
}

const FilterMenu = ({
  type,
  onFilter,
  isHistory,
}: {
  type: State['type']
  onFilter: Action['onFilter']
  isHistory: State['isHistory']
}) => {
  const { t } = useTranslation('myOwnedTickets')

  const handleSelect = useCallback(onFilter, [onFilter])

  const tags = isHistory
    ? [
        { value: UserPickupTicketStatus.All, label: t('all') },
        { value: UserPickupTicketStatus.Used, label: t('used') },
        { value: UserPickupTicketStatus.Expired, label: t('expired') },
      ]
    : [
        { value: UserPickupTicketStatus.All, label: t('all') },
        { value: UserPickupTicketStatus.Unused, label: t('unused') },
        { value: UserPickupTicketStatus.Used, label: t('used') },
        { value: UserPickupTicketStatus.Expired, label: t('expired') },
      ]

  return <ScrollTag value={type} onClick={handleSelect} tags={tags} />
}
